import React, { useState, useContext } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from '@mui/material';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/client';
import { toast } from 'react-toastify';

const CURRENCY_OPTIONS = [
  { code: 'USD', label: 'US Dollar (USD)' },
  { code: 'UGX', label: 'Ugandan Shilling (UGX)' },
  { code: 'KSH', label: 'Kenyan Shilling (KSH)' },
  { code: 'TSH', label: 'Tanzanian Shilling (TSH)' },
];

const UserSettings = () => {
  const { user, fetchUser } = useContext(AuthContext);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [emailDialog, setEmailDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [emailData, setEmailData] = useState({
    newEmail: '',
    password: '',
  });

  const [currency, setCurrency] = useState('UGX');
  const [savingCurrency, setSavingCurrency] = useState(false);

  React.useEffect(() => {
    if (user?.role === 'admin') {
      axios.get('/settings/defaultCurrency')
        .then(r => {
          if (r.data.value) setCurrency(r.data.value);
        })
        .catch(() => {});
    }
  }, [user]);

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`/users/${user._id}/change-password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      toast.success('Password updated successfully');
      setPasswordDialog(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error changing password');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = async () => {
    if (!emailData.newEmail || !emailData.newEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`/users/${user._id}/change-email`, {
        newEmail: emailData.newEmail,
        password: emailData.password,
      });
      toast.success('Email updated successfully');
      await fetchUser(); // Refresh user data
      setEmailDialog(false);
      setEmailData({
        newEmail: '',
        password: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error changing email');
    } finally {
      setLoading(false);
    }
  };

  const handleCurrencySave = async () => {
    setSavingCurrency(true);
    try {
      await axios.put('/settings/defaultCurrency', { value: currency });
      toast.success('Default currency updated');
    } catch (e) {
      toast.error('Failed to update currency');
    } finally {
      setSavingCurrency(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Account Settings
      </Typography>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Profile Information
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Name"
              value={user?.name || ''}
              disabled
              helperText="Contact administrator to change name"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email"
              value={user?.email || ''}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone"
              value={user?.phone || ''}
              disabled
              helperText="Contact administrator to change phone"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Role"
              value={user?.role || ''}
              disabled
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Security Settings
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setPasswordDialog(true)}
          >
            Change Password
          </Button>
          <Button
            variant="outlined"
            onClick={() => setEmailDialog(true)}
          >
            Change Email
          </Button>
        </Box>
      </Box>

      {user?.role === 'admin' && (
        <Box sx={{ mt: 5, p: 3, bgcolor: '#fafafa', borderRadius: 2, border: '1px solid #eee' }}>
          <Typography variant="h6" gutterBottom>Global System Settings</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Default Currency"
                value={currency}
                onChange={e => setCurrency(e.target.value)}
              >
                {CURRENCY_OPTIONS.map(opt => (
                  <MenuItem value={opt.code} key={opt.code}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button variant="contained" onClick={handleCurrencySave} disabled={savingCurrency}>
                {savingCurrency ? 'Saving...' : 'Save Currency'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Password Change Dialog */}
      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="password"
            label="Current Password"
            margin="normal"
            value={passwordData.currentPassword}
            onChange={(e) =>
              setPasswordData({ ...passwordData, currentPassword: e.target.value })
            }
          />
          <TextField
            fullWidth
            type="password"
            label="New Password"
            margin="normal"
            value={passwordData.newPassword}
            onChange={(e) =>
              setPasswordData({ ...passwordData, newPassword: e.target.value })
            }
            helperText="Minimum 6 characters"
          />
          <TextField
            fullWidth
            type="password"
            label="Confirm New Password"
            margin="normal"
            value={passwordData.confirmPassword}
            onChange={(e) =>
              setPasswordData({ ...passwordData, confirmPassword: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog(false)}>Cancel</Button>
          <Button
            onClick={handlePasswordChange}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Change Dialog */}
      <Dialog open={emailDialog} onClose={() => setEmailDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Email</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            You will need to enter your password to change your email address.
          </Alert>
          <TextField
            fullWidth
            type="email"
            label="New Email Address"
            margin="normal"
            value={emailData.newEmail}
            onChange={(e) =>
              setEmailData({ ...emailData, newEmail: e.target.value })
            }
          />
          <TextField
            fullWidth
            type="password"
            label="Confirm Password"
            margin="normal"
            value={emailData.password}
            onChange={(e) =>
              setEmailData({ ...emailData, password: e.target.value })
            }
            helperText="Enter your current password to confirm"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialog(false)}>Cancel</Button>
          <Button
            onClick={handleEmailChange}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default UserSettings;

